import React from "react";
import {
  X,
  TrendingUp,
  Users,
  Building,
  CheckCircle,
  Target,
  Award,
} from "lucide-react";

const HRProblemSolution = () => {
  const problems = [
    "Saying \"I'm passionate about HR\" isn't enough.",
    "A generic CV won't make it past HR (ironic, right?).",
    "Uploading on job portals ≠ interview calls.",
    'Following "HR tips" on LinkedIn ≠ getting a role.',
  ];

  return (
    <div className="bg-black py-32 lg:py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="space-y-12">
          {/* Problems Section - Styled like Performance Review */}
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="inline-block bg-slate-800/50 rounded-lg px-6 py-3 border border-slate-600/30 mb-4">
                <div className="text-xs text-gray-400 uppercase tracking-wider"></div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">
                  The Hard Truth About HR Opportunities
                </h2>
                <div className="text-xs text-red-400 mt-1">
                  Status: Needs Improvement
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:gap-6">
              {problems.map((problem, index) => (
                <div
                  key={index}
                  className="group relative bg-slate-900/50 border border-red-500/30 rounded-lg p-6 hover:border-red-400/50 transition-all duration-300"
                >
                  {/* Performance Review Style Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-red-500/20 rounded-full p-2">
                        <X className="w-4 h-4 text-red-400" />
                      </div>
                      <div>
                        <div className="text-xs flex items-start text-gray-400 uppercase">
                          Issue #{index + 1}
                        </div>
                        <div className="text-xs text-red-400">
                          Priority: High
                        </div>
                      </div>
                    </div>
                    <div className="bg-red-500/10 rounded-full px-3 py-1 text-xs text-red-400 border border-red-500/20">
                      BLOCKER
                    </div>
                  </div>
                  <div className="flex item-start">
                    <p className="text-gray-300 text-lg group-hover:text-white transition-colors pl-10">
                      {problem}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Solution Section - Redesigned for Sales Impact */}
          <div className="relative overflow-hidden rounded-xl bg-slate-900/30 border border-green-500/30 p-4 md:p-8">
            {/* Success Plan Header */}
            <div className="text-center mb-6 md:mb-8">
              <div className="inline-block bg-green-500/10 rounded-lg px-6 py-3 border border-green-500/30 mb-4">
                <div className="text-xs text-green-400 uppercase tracking-wider">
                  Solution Framework
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white">
                  You Need a Real System
                </h3>
                <div className="text-xs text-green-400 mt-1"></div>
              </div>
            </div>

            {/* Powerful Value Proposition - Both cards visible on mobile */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
              {/* First Card */}
              <div className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 rounded-lg p-4 md:p-6 border border-blue-500/30 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/10 rounded-full blur-2xl"></div>
                <div className="relative">
                  <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3 md:mb-4">
                    <div className="bg-blue-500/20 rounded-full p-2 md:p-3 self-center md:self-auto">
                      <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-blue-400" />
                    </div>
                    <div className="text-center md:text-left">
                      <div className="text-base md:text-lg font-bold text-white">
                        We've Figured It Out
                      </div>
                      <div className="text-xs md:text-sm text-blue-300">
                        The system that actually works
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-300 text-xs md:text-sm leading-relaxed text-center md:text-left">
                    Through deep research and real-world testing, we've
                    identified exactly what HR recruiters want and how to get
                    their attention.
                  </p>
                </div>
              </div>

              {/* Second Card - Now visible on mobile */}
              <div className="bg-gradient-to-br from-green-900/50 to-green-800/30 rounded-lg p-4 md:p-6 border border-green-500/30 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-green-500/10 rounded-full blur-2xl"></div>
                <div className="relative">
                  <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3 md:mb-4">
                    <div className="bg-green-500/20 rounded-full p-2 md:p-3 self-center md:self-auto">
                      <Target className="w-5 h-5 md:w-6 md:h-6 text-green-400" />
                    </div>
                    <div className="text-center md:text-left">
                      <div className="text-base md:text-lg font-bold text-white">
                        Now We're Sharing It
                      </div>
                      <div className="text-xs md:text-sm text-green-300">
                        Your complete roadmap to success
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-300 text-xs md:text-sm leading-relaxed text-center md:text-left">
                    Every strategy, template, and insider technique that
                    transforms generic applications into interview calls.
                  </p>
                </div>
              </div>
            </div>

            {/* Redesigned Program Promise - 2x2 grid on mobile */}
            <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl p-4 md:p-8 border border-slate-600/30 mb-6 md:mb-8 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-green-500/5 to-purple-500/5"></div>
              <div className="relative">
                <div className="text-center mb-4 md:mb-6">
                  <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">
                    Your Success Blueprint
                  </div>
                  <h4 className="text-xl md:text-2xl font-bold text-white mb-2">
                    The Complete HR Career Transformation
                  </h4>
                  <p className="text-sm md:text-base text-gray-300">
                    Everything you need to land your dream HR role
                  </p>
                </div>

                {/* Mobile: 2x2 grid, Desktop: 4 columns */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
                  <div className="text-center group">
                    <div className="bg-blue-500/10 rounded-full w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-2 md:mb-3 group-hover:bg-blue-500/20 transition-colors">
                      <span className="text-2xl md:text-3xl font-bold text-blue-400">
                        4
                      </span>
                    </div>
                    <div className="text-xs md:text-sm font-medium text-white">
                      Intensive Weeks
                    </div>
                    <div className="text-xs text-gray-400 mt-1 hidden md:block">
                      Focused learning
                    </div>
                  </div>

                  <div className="text-center group">
                    <div className="bg-green-500/10 rounded-full w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-2 md:mb-3 group-hover:bg-green-500/20 transition-colors">
                      <span className="text-2xl md:text-3xl font-bold text-green-400">
                        8
                      </span>
                    </div>
                    <div className="text-xs md:text-sm font-medium text-white">
                      Live Sessions
                    </div>
                    <div className="text-xs text-gray-400 mt-1 hidden md:block">
                      Expert guidance
                    </div>
                  </div>

                  <div className="text-center group">
                    <div className="bg-purple-500/10 rounded-full w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-2 md:mb-3 group-hover:bg-purple-500/20 transition-colors">
                      <span className="text-2xl md:text-3xl font-bold text-purple-400">
                        0
                      </span>
                    </div>
                    <div className="text-xs md:text-sm font-medium text-white">
                      Fluff Content
                    </div>
                    <div className="text-xs text-gray-400 mt-1 hidden md:block">
                      Pure value only
                    </div>
                  </div>

                  <div className="text-center group">
                    <div className="bg-amber-500/10 rounded-full w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-2 md:mb-3 group-hover:bg-amber-500/20 transition-colors">
                      <span className="text-lg md:text-2xl font-bold text-amber-400">
                        100%
                      </span>
                    </div>
                    <div className="text-xs md:text-sm font-medium text-white">
                      Results Focus
                    </div>
                    <div className="text-xs text-gray-400 mt-1 hidden md:block">
                      Job-ready skills
                    </div>
                  </div>
                </div>

                {/* Success Guarantee - Hidden on mobile */}
                <div className="mt-4 md:mt-6 text-center hidden md:block">
                  <div className="inline-flex items-center gap-2 bg-green-500/10 rounded-full px-4 py-2 border border-green-500/20">
                    <Award className="w-4 h-4 text-green-400" />
                    <span className="text-sm font-medium text-green-300">
                      Battle-Tested Strategies
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Core Competencies - Reduced padding on mobile */}
            <div className="grid grid-cols-3 md:grid-cols-3 gap-2 md:gap-4">
              <div className="text-center p-2 md:p-6 bg-slate-800/30 rounded-lg border border-slate-600/20 hover:border-blue-500/30 transition-all group">
                <div className="bg-blue-500/10 rounded-full w-8 h-8 md:w-12 md:h-12 flex items-center justify-center mx-auto mb-1 md:mb-3 group-hover:bg-blue-500/20 transition-colors">
                  <Building className="w-4 h-4 md:w-6 md:h-6 text-blue-400" />
                </div>
                <div className="text-xs md:text-sm font-medium text-white mb-1">
                  Strategic Approach
                </div>
                <div className="text-xs text-gray-400 hidden md:block">
                  People-first methodology that actually works
                </div>
              </div>

              <div className="text-center p-2 md:p-6 bg-slate-800/30 rounded-lg border border-slate-600/20 hover:border-green-500/30 transition-all group">
                <div className="bg-green-500/10 rounded-full w-8 h-8 md:w-12 md:h-12 flex items-center justify-center mx-auto mb-1 md:mb-3 group-hover:bg-green-500/20 transition-colors">
                  <Users className="w-4 h-4 md:w-6 md:h-6 text-green-400" />
                </div>
                <div className="text-xs md:text-sm font-medium text-white mb-1">
                  Network Building
                </div>
                <div className="text-xs text-gray-400 hidden md:block">
                  Connect with the right people, the right way
                </div>
              </div>

              <div className="text-center p-2 md:p-6 bg-slate-800/30 rounded-lg border border-slate-600/20 hover:border-purple-500/30 transition-all group">
                <div className="bg-purple-500/10 rounded-full w-8 h-8 md:w-12 md:h-12 flex items-center justify-center mx-auto mb-1 md:mb-3 group-hover:bg-purple-500/20 transition-colors">
                  <TrendingUp className="w-4 h-4 md:w-6 md:h-6 text-purple-400" />
                </div>
                <div className="text-xs md:text-sm font-medium text-white mb-1">
                  Career Acceleration
                </div>
                <div className="text-xs text-gray-400 hidden md:block">
                  Fast-track your HR career growth
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRProblemSolution;
